import React from "react";
import styles from "./ota-configuration-history.module.scss";
import plusIcon from "@src/assets/svg/plus-icon.svg";
import OTAModal from "../modals/ota-modal/ota-modal";
import { TabButtons } from "@src/components/atoms/tab-buttons/tab-buttons";
import {
  OTA_CONFIGURATION_HISTORY as cssClassName,
  CONFIGURATION_TABS,
  CONFIGURATIONS,
  CONFIGURATION_BACKUPS,
} from "../../../cloud-connect/ota-update/constants";
import { ButtonWithIcon } from "@src/components/atoms/buttons/button-with-icon/button-with-icon";
import { OTAHistoryList } from "../ota-history-list/ota-history-list";
import { ListItem } from "@src/types/ota-update";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";
import { useOTAConfigurationHistory } from "./use-configuration-history";

type TabType = (typeof CONFIGURATION_TABS)[keyof typeof CONFIGURATION_TABS];

interface OTAConfigurationHistoryProps {
  activeOTAHistoryTab: TabType;
  setActiveOTAHistoryTab: React.Dispatch<React.SetStateAction<TabType>>;
  configurations: ListItem[] | [];
  backups: ListItem[] | [];
  onInstallStarted: (updateId: string) => void;
  fetchMainList: () => Promise<void>;
  fetchBackups: () => Promise<void>;
  setIsConfigurationLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsBackupLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const OTAConfigurationHistory = ({
  activeOTAHistoryTab,
  setActiveOTAHistoryTab,
  configurations,
  backups,
  onInstallStarted,
  fetchMainList,
  fetchBackups,
  setIsConfigurationLoading,
  setIsBackupLoading,
}: OTAConfigurationHistoryProps) => {
  const {
    texts,
    tabLabels,
    isModalOpen,
    setIsModalOpen,
    selectedRow,
    setSelectedRow,
    handleTabChange,
    closeFirmwareModal,
    showOTAConfigurationDrawer,
    showOTAConfigurationBackupDrawer,
    getPermission,
  } = useOTAConfigurationHistory({
    setActiveOTAHistoryTab,
    setIsConfigurationLoading,
    setIsBackupLoading,
  });

  const isConfigurationsTab =
    activeOTAHistoryTab === CONFIGURATION_TABS.CONFIGURATIONS;

  const activeButtonText = isConfigurationsTab
    ? texts.uploadConfigurationsReleaseText
    : texts.createBackupText;

  const handleActiveTabActionClick: (
    e?: React.MouseEvent | React.KeyboardEvent,
  ) => void = () => {
    if (isConfigurationsTab) {
      showOTAConfigurationDrawer();
      return;
    }

    showOTAConfigurationBackupDrawer();
  };

  const activeTabContent = isConfigurationsTab ? (
    <OTAHistoryList
      key={CONFIGURATIONS}
      type={CONFIGURATIONS}
      setIsModalOpen={setIsModalOpen}
      setSelectedRow={setSelectedRow}
      list={configurations}
      activeOTAHistoryTab={activeOTAHistoryTab}
      showingAnyBlock={false}
    />
  ) : (
    <OTAHistoryList
      key={CONFIGURATION_BACKUPS}
      type={CONFIGURATION_BACKUPS}
      setIsModalOpen={setIsModalOpen}
      setSelectedRow={setSelectedRow}
      list={backups}
      activeOTAHistoryTab={activeOTAHistoryTab}
      showingAnyBlock={false}
    />
  );

  return (
    <>
      <div className={styles[cssClassName]}>
        <div className={styles[`${cssClassName}__header`]}>
          <div>
            <TabButtons
              tabValue={tabLabels[activeOTAHistoryTab]}
              callback={handleTabChange}
              tabs={Object.values(tabLabels)}
            />
          </div>

          {activeOTAHistoryTab === CONFIGURATION_TABS.CONFIGURATIONS && (
            <RenderIfUserHasPermission
              permission={getPermission(activeOTAHistoryTab)}
            >
              <ButtonWithIcon
                icon={plusIcon}
                fun={
                  handleActiveTabActionClick as (
                    e?:
                      | React.MouseEvent<Element, MouseEvent>
                      | React.KeyboardEvent<Element>,
                  ) => void
                }
                text={activeButtonText}
                variant={["no-outline-with-green20-hover", "color-blue"]}
              />
            </RenderIfUserHasPermission>
          )}
        </div>

        {activeTabContent}
      </div>

      <OTAModal
        isOpen={isModalOpen}
        closeFirmwareModal={closeFirmwareModal}
        selectedRow={selectedRow}
        onInstallStarted={onInstallStarted}
        fetchMainList={fetchMainList}
        fetchBackups={fetchBackups}
      />
    </>
  );
};

export default OTAConfigurationHistory;
